/* eslint-disable max-lines-per-function */
import { buildDeleteErrorOutput } from '@/domain/termine/hooks/utils/deleteTerminErrorMessage';
import { useDeleteTermin } from '@/domain/termine/hooks/useDeleteTermin';
import { Termin } from '@/domain/termine/shared/types';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

const VERKNUEPFUNG_ERROR_TYPES = [
  'termine_terminDeleteSchichtenVerknuepfungExistError',
  'termine_terminDeleteArbeitszeitenVerknuepfungExistError',
];

function getDeleteErrorLabels(t: ReturnType<typeof useTranslation>['t']) {
  return {
    schichtOne: t('entities.shift_one'),
    schichtOther: t('entities.shift_other'),
    azOne: t('entities.workingHour_one'),
    azOther: t('entities.workingHour_other'),
  };
}

export function useTerminDeleteHandler(termin: Termin) {
  const { t } = useTranslation();
  const { deleteTermin, loading } = useDeleteTermin();
  const isDeleteDisabled =
    termin.hasHiddenSchichten || termin.hasHiddenArbeitszeiten;

  const handleDelete = async () => {
    const deleteResult = await deleteTermin(termin.id);
    const isVerknuepfungsError = VERKNUEPFUNG_ERROR_TYPES.includes(
      deleteResult.errorType ?? '',
    );
    if (isDeleteDisabled) {
      toast({
        title: t('domain.termine.deleteDialog.deleteTermin'),
        description: t('domain.termine.deleteWithHiddenArbeitsbereich'),
      });
      return false;
    }
    if (isVerknuepfungsError) {
      const { toastDescription } = buildDeleteErrorOutput({
        message:
          deleteResult.error?.message ||
          t('domain.termine.deleteDialog.error.description', {
            name: termin.titel,
          }),
        termin,
        isVerknuepfungsError,
        labels: getDeleteErrorLabels(t),
      });
      toast({
        title: t('domain.termine.deleteDialog.deleteTerminFailure'),
        description: toastDescription,
      });
      return false;
    }
    if (deleteResult?.error) {
      toast({
        title: t('domain.termine.deleteDialog.deleteTerminFailure'),
        description: t('domain.termine.deleteDialog.error.description', {
          name: termin.titel,
        }),
      });
      return false;
    }

    toast({
      title: t('domain.termine.deleteDialog.deleteTermin'),
      description: t('domain.termine.deleteDialog.success.description', {
        name: termin.titel,
      }),
    });
    return true;
  };
  return { handleDelete, loading };
}
